/**
 * Draft-Order Lotto - AUDIO-GATED card flip reveal.
 *
 * [ADAM AUDIO batch 2 item 1] Timing used to be a blind 1.8s pause then a
 * fixed 1s per card, with Adam silent. Now Adam calls the order and the
 * cards follow HIM: each team's line is enqueued, and that card flips only
 * when the clip's gating promise resolves (i.e. the audio actually ended).
 * Nothing overlaps, ever - the promise comes from the serialized queue in
 * `audio/auctioneerAudio.js`, so a slow synth delays the flip instead of
 * desyncing from it.
 *
 * EVERY client runs this same reveal (founder Q2a) - it is the shared hype
 * moment, not a commissioner-only cutscene. Each screen is driven by its
 * own audio, so screens can drift a few hundred ms; that is accepted and
 * deliberately NOT frame-synced. Only the commissioner gets the advance
 * button; everyone else waits on them.
 *
 * Music is ducked to 50% for the reveal via `lobbyAmbient` (NOT the Adam
 * voice volume) and restored when the draft actually starts.
 */
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { enqueue, prefetchTts, stopAll } from "../audio/auctioneerAudio";
import { duckAmbient } from "../audio/lobbyAmbient";
import { revealIntroLine, revealLines } from "../utils/revealTemplates";

const ORDINALS = ["1ST","2ND","3RD","4TH","5TH","6TH","7TH","8TH","9TH","10TH","11TH","12TH"];

// ── Single flip card ───────────────────────────────────────────────────────

function LottoCard({ ordinal, teamName, isMe, isActive, isRevealed, isDark }) {
  const cardBg     = isDark ? "#1C1C1C" : "#FFFFFF";
  const cardBorder = isDark ? "#2A2A2A" : "#D0D0D0";
  const teamText   = isDark ? "#F5F5F5" : "#0A0A0A";
  const backHint   = isDark ? "rgba(255,107,0,0.12)" : "rgba(255,107,0,0.18)";
  // Track whether this card was naturally activated (vs. instant-skip)
  const wasActivated = useRef(false);
  if (isActive && !wasActivated.current) wasActivated.current = true;

  // Flip duration: 1s for natural reveal, 0.15s for skip
  const flipDuration = wasActivated.current ? 1 : (isRevealed ? 0.15 : 0);

  return (
    <div style={{ perspective: "1000px" }}>
      <motion.div
        animate={{ rotateY: isRevealed ? 180 : 0 }}
        transition={{ duration: flipDuration, ease: [0.4, 0, 0.2, 1] }}
        style={{
          transformStyle: "preserve-3d",
          position: "relative",
          minHeight: "7rem",
        }}
      >
        {/* ── Back face - inline styles to prevent iOS Safari theme override ── */}
        <div
          style={{
            backfaceVisibility: "hidden",
            position: "absolute", inset: 0,
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: "0.75rem",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <span style={{
            fontFamily: "var(--font-display, 'Barlow Semi Condensed', sans-serif)",
            fontWeight: 900, fontSize: "1.5rem", textTransform: "uppercase",
            letterSpacing: "0.15em", color: backHint,
            userSelect: "none", pointerEvents: "none",
          }}>
            PGF
          </span>
        </div>

        {/* ── Front face - theme-aware bg/text, orange highlight for "you" ── */}
        <div
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            position: "absolute", inset: 0,
            background: isMe ? "rgba(255,107,0,0.12)" : cardBg,
            border: isMe ? "1px solid rgba(255,107,0,0.70)" : `1px solid ${cardBorder}`,
            borderRadius: "0.75rem",
            padding: "1rem",
            display: "flex", flexDirection: "column", justifyContent: "space-between",
            boxShadow: isMe ? "0 0 30px rgba(255,107,0,0.25)" : undefined,
          }}
        >
          <p style={{ fontFamily: "var(--font-display, sans-serif)", fontWeight: 900, fontSize: "0.65rem",
            textTransform: "uppercase", letterSpacing: "0.1em", color: "#FF6B00", marginBottom: "0.25rem" }}>
            {ordinal}
          </p>
          <p style={{
            fontFamily: "var(--font-display, sans-serif)", fontWeight: 900,
            // Shrink font for longer names so they stay inside the card
            fontSize: teamName.length > 16 ? "0.75rem" : teamName.length > 12 ? "0.9rem" : "1.1rem",
            textTransform: "uppercase", lineHeight: 1.15, color: teamText,
            wordBreak: "break-word",
          }}>
            {teamName}
          </p>
          {isMe && (
            <p style={{ fontFamily: "var(--font-display, sans-serif)", fontWeight: 900, fontSize: "0.6rem",
              textTransform: "uppercase", color: "#FF6B00", marginTop: "0.25rem", letterSpacing: "0.1em" }}>
              ← You
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────

export default function NominationLottoModal({
  nominationOrder,
  managers,
  myUserId,
  onComplete,
  format = "auction",
  canAdvance = false,
  onRevealProgress,
}) {
  const { isDark } = useTheme();
  const [revealedCount, setRevealedCount] = useState(0);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  // [BUG-D] A boolean cancel flag could not kill a prior loop: the new
  // run reset it to false before the old loop reached its next await
  // checkpoint, so BOTH loops kept enqueuing and every team was announced
  // twice (worse with a slow clip, which widens the interleave window).
  // Monotonic run token: only the newest invocation may enqueue.
  const runIdRef = useRef(0);
  // Belt-and-braces: a reveal slot is announced at most once per order,
  // regardless of mechanism. Keyed by reveal index (-1 = intro line).
  const announcedRef = useRef({ key: null, set: new Set() });

  const ordered = nominationOrder.map(uid =>
    managers.find(m => m.user_id === uid) || { team_name: "Unknown", user_id: uid }
  );
  const orderKey = nominationOrder.join(",");

  // ── The audio-gated reveal: intro line, then one line per team, each
  // card flipping on ITS clip ending. Runs identically on every client.
  useEffect(() => {
    const myRun = ++runIdRef.current;
    const isStale = () => runIdRef.current !== myRun;
    if (announcedRef.current.key !== orderKey) {
      announcedRef.current = { key: orderKey, set: new Set() };
    }
    const announced = announcedRef.current.set;
    const teamNames = ordered.map((m) => m.team_name || "Unknown");
    const lines = revealLines(teamNames);

    (async () => {
      duckAmbient(0.5);
      // Warm EVERY line in parallel the moment the reveal starts. The
      // queue only pre-warms one line ahead, which is not enough for a
      // 12-team reveal: the gap between flips must be Adam breathing,
      // never ElevenLabs thinking.
      const intro = revealIntroLine(format);
      const warm = [intro, ...lines].map((t) => prefetchTts(t));

      // [BOARD MIRROR] Publish the absolute reveal position so a standalone
      // board animates in lockstep. No-ops for non-pacer clients.
      onRevealProgress(0);
      if (!announced.has(-1)) {
        announced.add(-1);
        await enqueue({ group: "REVEAL", text: intro, fetchPromise: warm[0] });
        if (isStale()) return;
      }
      setStarted(true);
      for (let i = 0; i < lines.length; i += 1) {
        if (announced.has(i)) {
          // Already voiced by a run that got superseded mid-flight - keep the
          // cards in sync without replaying the line.
          setRevealedCount(i + 1);
          onRevealProgress(i + 1);
          continue;
        }
        announced.add(i);
        // Gating promise: resolves when THIS clip ends. The flip is next.
        await enqueue({
          group: "REVEAL", text: lines[i], fetchPromise: warm[i + 1],
        });
        if (isStale()) return;
        setRevealedCount(i + 1);
        onRevealProgress(i + 1);
      }
      setDone(true);
    })();

    // Bumping the token invalidates this run for every downstream await.
    return () => { runIdRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey, format]);

  const skipAll = () => {
    runIdRef.current += 1;
    stopAll();
    setStarted(true);
    setRevealedCount(ordered.length);
    onRevealProgress(ordered.length);
    setDone(true);
  };

  const gridCols = ordered.length <= 6
    ? "grid-cols-2 sm:grid-cols-3"
    : ordered.length <= 9
    ? "grid-cols-3"
    : "grid-cols-5";

  // Theme-aware tokens
  const overlayBg   = isDark ? "rgba(0,0,0,0.94)" : "rgba(240,240,240,0.97)";
  const radialGlow  = isDark
    ? "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(255,107,0,0.08) 0%, transparent 70%)"
    : "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(255,107,0,0.10) 0%, transparent 70%)";
  const titleColor  = isDark ? "#F5F5F5" : "#0A0A0A";
  const titleShadow = isDark ? "0 0 60px rgba(255,107,0,0.15)" : "0 0 40px rgba(255,107,0,0.20)";
  const subtleText  = isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.45)";
  const skipBorder  = isDark ? "border-white/[0.14]" : "border-black/[0.14]";
  const skipText    = isDark ? "text-white/35 hover:text-white/60" : "text-black/45 hover:text-black/70";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: overlayBg }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: radialGlow }} />

      <div className="relative w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-center mb-8"
        >
          <p className="font-display font-black text-xs uppercase tracking-[0.45em] text-[#FF6B00] mb-2">
            Draft Night
          </p>
          <h2
            className="font-display font-black uppercase leading-none"
            style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)", color: titleColor, textShadow: titleShadow }}
          >
            {format === "snake" ? "Picking Order" : "Nomination Order"}
          </h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="font-body text-sm mt-2"
            style={{ color: subtleText }}
          >
            {done && !canAdvance
              ? "Waiting on Commissioner to Start Draft..."
              : done
              ? "The order is locked. It's time."
              : started
              ? `${revealedCount} of ${ordered.length} revealed`
              : "Adam's got the card..."}
          </motion.p>
        </motion.div>

        <div className={`grid ${gridCols} gap-2.5 mb-8`}>
          {ordered.map((mgr, i) => (
            <LottoCard
              key={i}
              ordinal={ORDINALS[i]}
              teamName={mgr.team_name}
              isMe={mgr.user_id === myUserId}
              isActive={i === revealedCount - 1}
              isRevealed={i < revealedCount}
              isDark={isDark}
            />
          ))}
        </div>

        <div className="flex gap-3">
          {/* Skip + LET'S DRAFT are commissioner-only: everyone sees and
              hears the reveal, but only the commissioner advances. */}
          {canAdvance && (
            <button
              onClick={skipAll}
              className={`flex-1 border ${skipBorder} ${skipText} font-display font-bold uppercase text-sm py-3 rounded-xl transition-all`}
            >
              Skip
            </button>
          )}

          {!canAdvance ? (
            <div className="flex-1 flex items-center justify-center py-3">
              <p
                className="font-display font-bold text-sm uppercase tracking-widest animate-pulse"
                style={{ color: subtleText }}
              >
                Waiting on Commissioner to Start Draft...
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {done && (
                <motion.button
                  initial={{ scale: 0.80, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.4 }}
                  onClick={onComplete}
                  data-testid="lotto-start-btn"
                  className="flex-[2] bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase text-xl py-3.5 rounded-xl transition-colors active:scale-95"
                  style={{ boxShadow: "0 0 60px rgba(255,107,0,0.55)" }}
                >
                  Let's Draft!
                </motion.button>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    </motion.div>
  );
}
